import { useParams } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { useDeviceDisplay } from "../hooks/useDeviceDisplay";
import type {
  DeviceDisplayStatus
} from "../hooks/useDeviceDisplay";
import type { DisplayPhase, WallSnapshot } from "../../../src/display/types";
import HexGridBackground from "../components/HexGridBackground";
import "./DeviceDisplay.css";

const PHASE_COPY: Record<DisplayPhase, string> = {
  checked_out: "Checked out for preparation",
  downloading: "Downloading application",
  waiting_install_slot: "Waiting for install slot",
  installing_main: "Installing application",
  installing_test: "Installing test build",
  device_setup: "Preparing device",
  testing: "Running tests",
  cleanup_apps: "Removing applications",
  cleanup_animations: "Restoring animations",
  cleanup_notifications: "Clearing notifications",
  available: "Idle — waiting for a test",
  maintenance: "Under maintenance",
  offline: "Device offline"
};

const CONNECTION_COPY: Record<DeviceDisplayStatus, string> = {
  connecting: "Connecting",
  live: "Live",
  reconnecting: "Reconnecting…",
  offline: "Connection lost — showing last known status",
  inactive: "Standby",
  expired: "Session ended"
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes;
  let unit = "B";
  for (const next of units) {
    value = value / 1024;
    unit = next;
    if (value < 1024 || next === "GB") break;
  }
  return `${value.toFixed(1)} ${unit}`;
}

function relativeTime(iso: string): string {
  const timestamp = Date.parse(iso);
  if (!Number.isFinite(timestamp)) return "unknown";
  try {
    return formatDistanceToNowStrict(timestamp, { addSuffix: true });
  } catch {
    return "unknown";
  }
}

// devices.os_version is the bare release ("16", "18.0"). iOS hosts report
// models like "iPhone 15 Pro" / "iPhone15,2"; everything else is Android.
function formatOsVersion(osVersion: string, model: string | null): string {
  if (!/^\d/.test(osVersion)) return osVersion;
  const isIos = model !== null && /^(iPhone|iPad|iPod)/.test(model);
  return isIos ? `iOS ${osVersion}` : `Android ${osVersion}`;
}

function deviceType(snapshot: WallSnapshot): string {
  const parts = [
    snapshot.model,
    snapshot.osVersion
      ? formatOsVersion(snapshot.osVersion, snapshot.model)
      : null
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : "Unknown device";
}

function phaseCopy(
  status: DeviceDisplayStatus,
  snapshot: WallSnapshot | null
): string {
  if (status === "expired") return "Display session ended";
  if (status === "inactive") return "Awaiting assignment";
  if (snapshot) return PHASE_COPY[snapshot.phase];
  return "Loading device status";
}

function OwnerLine({ snapshot }: { snapshot: WallSnapshot | null }) {
  if (!snapshot || snapshot.owner.kind === "unassigned") {
    return <p className="dd-owner">Unassigned</p>;
  }
  if (snapshot.owner.kind === "human") {
    return <p className="dd-owner">{snapshot.owner.email}</p>;
  }
  const { project, job } = snapshot.owner;
  if (!project && !job) {
    return <p className="dd-owner">Automation</p>;
  }
  return (
    <p className="dd-owner">
      <span className="dd-owner-part">{project ?? "Unknown project"}</span>
      {job ? (
        <span className="dd-owner-part dd-owner-job">{job}</span>
      ) : null}
    </p>
  );
}

function DownloadDetail({ snapshot }: { snapshot: WallSnapshot | null }) {
  const download = snapshot?.download ?? null;
  if (!download) return null;
  const trustworthyTotal =
    download.total != null && download.total > 0 && download.total >= download.bytes;
  if (!trustworthyTotal) {
    return (
      <p className="dd-download" data-testid="download-detail">
        {formatBytes(download.bytes)} downloaded · total unknown
      </p>
    );
  }
  const percent = Math.min(
    100,
    Math.floor((download.bytes / (download.total as number)) * 100)
  );
  return (
    <p
      className="dd-download"
      data-testid="download-detail"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Download progress"
    >
      <span className="dd-bar" aria-hidden="true">
        <span className="dd-bar-fill" style={{ width: `${percent}%` }} />
      </span>
      {percent}% · {formatBytes(download.bytes)} of{" "}
      {formatBytes(download.total as number)}
    </p>
  );
}

function DeviceIdentity({ snapshot }: { snapshot: WallSnapshot | null }) {
  if (!snapshot) return null;
  return (
    <dl className="dd-identity" data-testid="device-identity">
      <div>
        <dt>Host</dt>
        <dd data-testid="identity-host">
          {snapshot.hostName ?? "Unknown host"}
        </dd>
      </div>
      <div>
        <dt>Device</dt>
        <dd data-testid="identity-device-type">{deviceType(snapshot)}</dd>
      </div>
      <div>
        <dt>Serial</dt>
        <dd data-testid="identity-serial">{snapshot.serial}</dd>
      </div>
      <div className="dd-identity-id">
        <dt>Device ID</dt>
        <dd data-testid="identity-device-id">{snapshot.deviceId}</dd>
      </div>
    </dl>
  );
}

export default function DeviceDisplay() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { status, snapshot, identify } = useDeviceDisplay(sessionId ?? "");
  const showStale =
    status === "offline" || status === "reconnecting" || status === "expired";

  return (
    <div
      className={`device-display dd-status-${status}`}
      data-testid="display-root"
      data-phase={snapshot?.phase ?? status}
    >
      <HexGridBackground className="dd-hexgrid" />
      <div className="dd-panel">
        <header className="dd-header">
          <span
            className="dd-connection"
            data-testid="connection-status"
            role="status"
            aria-live="polite"
          >
            <span className={`dd-dot dd-dot-${status}`} aria-hidden="true" />
            {CONNECTION_COPY[status]}
          </span>
        </header>
        <OwnerLine snapshot={snapshot} />
        <main className="dd-primary">
          <p className="dd-phase" data-testid="phase-copy" aria-live="polite">
            {phaseCopy(status, snapshot)}
          </p>
          <DownloadDetail snapshot={snapshot} />
        </main>
        <DeviceIdentity snapshot={snapshot} />
        <footer className="dd-meta">
          {(snapshot?.app || snapshot?.runId) && (
            <span className="dd-context">
              {snapshot.app ? (
                <span className="dd-chip" data-testid="app-name">
                  {snapshot.app}
                </span>
              ) : null}
              {snapshot.runId ? (
                <span className="dd-chip" data-testid="run-id">
                  {snapshot.runId}
                </span>
              ) : null}
            </span>
          )}
          {snapshot ? (
            <span className="dd-updated">
              Updated{" "}
              <time data-testid="updated-time" dateTime={snapshot.updatedAt}>
                {relativeTime(snapshot.updatedAt)}
              </time>
            </span>
          ) : null}
        </footer>
        {showStale && snapshot ? (
          <p className="dd-stale" aria-hidden="true">
            Last known status
          </p>
        ) : null}
      </div>
      {identify ? (
        <section className="dd-identify" data-testid="identify-overlay" aria-live="assertive">
          <p className="dd-identify-label">THIS PHONE</p>
          <p className="dd-identify-serial">{identify.serial}</p>
          <p className="dd-identify-model">{identify.model}</p>
          <p className="dd-identify-meta">{identify.osVersion}</p>
          {identify.wallLocation ? <p className="dd-identify-location">{identify.wallLocation}</p> : null}
        </section>
      ) : null}
    </div>
  );
}
